import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BROCHURE, EVENT, WHATSAPP_URL } from "@/lib/site";

const BrochureContext = createContext(() => {});
export const useBrochureGate = () => useContext(BrochureContext);

export function BrochureProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  const trigger = useCallback(() => {
    setSent(false);
    setError("");
    setOpen(true);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/leads", { name: form.name, email: form.email, phone: form.phone, source: "brochure" });
      setSent(true);
      const a = document.createElement("a");
      a.href = BROCHURE;
      a.download = "OFIC-2026-Brochure.pdf";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setError(err.response?.data?.detail || "Please check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <BrochureContext.Provider value={trigger}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="brochure-modal-overlay"
            className="fixed inset-0 z-[70] grid place-items-center bg-[#0b2b21]/60 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          >
            <motion.div
              data-testid="brochure-modal"
              role="dialog"
              aria-label="Download the OFIC 2026 brochure"
              className="w-full max-w-md rounded-2xl bg-[#f3f3e9] p-7"
              initial={{ y: 26, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 16, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <button data-testid="brochure-modal-close" aria-label="Close" onClick={() => setOpen(false)} className="float-right text-xl leading-none opacity-60 hover:opacity-100">✕</button>
              <span className="eyebrow text-[#5c6b5a]">OFIC 2026 / Brochure</span>
              <h3 className="mt-2 font-display text-2xl">
                Get the full <em className="italic">story.</em>
              </h3>
              {!sent ? (
                <form onSubmit={submit} className="mt-5 grid gap-3.5" data-testid="brochure-lead-form">
                  <p className="text-[13px] leading-relaxed text-[#5c6b5a]">
                    Tell us where to send it — the brochure covers participation packages, the expo and the awards. Your download starts right away.
                  </p>
                  <div>
                    <label className="field-label" htmlFor="lead-name">Full name *</label>
                    <input id="lead-name" data-testid="brochure-lead-name" className="field-input" required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="name" />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="lead-email">Work email *</label>
                    <input id="lead-email" data-testid="brochure-lead-email" className="field-input" type="email" required maxLength={180} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="lead-phone">Phone (optional)</label>
                    <input id="lead-phone" data-testid="brochure-lead-phone" className="field-input" type="tel" maxLength={25} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} autoComplete="tel" />
                  </div>
                  {error && <p data-testid="brochure-lead-error" className="text-[13px] font-bold text-red-800">{error}</p>}
                  <button data-testid="brochure-lead-submit" type="submit" disabled={busy} className="ofic-btn ofic-btn-forest justify-center">
                    {busy ? "Preparing your copy…" : "Download the brochure"} <span className="arrow">↓</span>
                  </button>
                  <small className="text-[11px] text-[#5c6b5a]">By sharing your email you agree Tefla's may contact you about OFIC 2026.</small>
                </form>
              ) : (
                <div className="mt-5" data-testid="brochure-lead-success">
                  <p className="rounded-lg p-4 text-sm font-bold" style={{ background: "var(--lime)" }}>
                    Your download has started. We've saved your copy — the OFIC team may reach out with participation details.
                  </p>
                  <Link to="/register" onClick={() => setOpen(false)} className="ofic-btn ofic-btn-forest mt-4 justify-center" data-testid="brochure-lead-register-cta">
                    While you're here — register <span className="arrow">↗</span>
                  </Link>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </BrochureContext.Provider>
  );
}

export function ExitPopup() {
  const [open, setOpen] = useState(false);
  const shown = useRef(sessionStorage.getItem("ofic_exit_shown") === "1");

  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) return undefined;
    const fire = () => {
      if (shown.current) return;
      shown.current = true;
      sessionStorage.setItem("ofic_exit_shown", "1");
      setOpen(true);
    };
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) fire();
    };
    let timeout;
    const onScroll = () => {
      const progress = window.scrollY / Math.max(1, document.body.scrollHeight - window.innerHeight);
      if (progress > 0.55) {
        timeout = setTimeout(fire, 4000);
        window.removeEventListener("scroll", onScroll);
      }
    };
    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timeout);
    };
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="exit-popup-overlay"
          className="fixed inset-0 z-[80] grid place-items-center bg-[#0b2b21]/70 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setOpen(false)}
        >
          <motion.div
            data-testid="exit-popup"
            role="dialog"
            aria-label="Early bird reminder"
            className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-[#123d30] p-8 text-[#f3f3e9]"
            initial={{ y: 34, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <button data-testid="exit-popup-close" aria-label="Close" onClick={() => setOpen(false)} className="absolute right-4 top-4 text-xl opacity-70 hover:opacity-100">✕</button>
            <span className="badge-lime">EARLY BIRD · UNTIL 10 NOV 2026</span>
            <h3 className="mt-4 font-display text-3xl leading-snug sm:text-4xl">
              Leaving? Your <em className="italic text-[#d7ef8b]">early-bird rate</em> won't wait.
            </h3>
            <p className="mt-3 text-sm leading-relaxed opacity-90">
              Delegate participation is {EVENT.date} at {EVENT.venue}. Early-bird delegates save ₹2,500 ($500) per pass — standard rates apply from 11 November 2026.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/register" data-testid="exit-popup-register" className="ofic-btn ofic-btn-lime">
                Lock the early-bird rate <span className="arrow">↗</span>
              </Link>
              <a href={BROCHURE} target="_blank" rel="noreferrer" data-testid="exit-popup-brochure" className="ofic-btn ofic-btn-cream">
                Download brochure <span className="arrow">↓</span>
              </a>
            </div>
            <p className="mt-5 text-[11px] opacity-60">One-day conclave · Conference, expo, networking & awards</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function WhatsAppButton() {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noreferrer"
      data-testid="whatsapp-chat-button"
      aria-label="Chat with the OFIC team on WhatsApp"
      className="fixed bottom-[84px] right-4 z-50 flex items-center gap-2 rounded-full py-3 pl-3.5 pr-5 shadow-xl md:bottom-5 md:right-5"
      style={{ background: "#1faa53", color: "#fff" }}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12.04 2a9.9 9.9 0 0 0-8.4 15.2L2 22l4.9-1.6A9.9 9.9 0 1 0 12.04 2Zm5.8 14.06c-.24.68-1.4 1.3-1.94 1.34-.5.05-.98.24-3.3-.68-2.78-1.1-4.55-3.94-4.69-4.12-.14-.18-1.13-1.5-1.13-2.86 0-1.36.71-2.03.97-2.3.24-.28.52-.35.7-.35h.5c.16 0 .38-.06.6.45.24.55.8 1.9.86 2.04.07.14.11.3.02.48-.09.18-.14.3-.27.45-.14.17-.29.38-.41.5-.14.14-.28.29-.12.57.16.28.72 1.18 1.54 1.92 1.06.94 1.95 1.24 2.23 1.38.28.14.44.12.6-.07.17-.18.7-.8.88-1.08.18-.28.36-.23.6-.14.25.1 1.57.74 1.84.87.28.14.46.2.53.32.07.11.07.66-.17 1.32Z" />
      </svg>
      <span className="text-[13px] font-extrabold">WhatsApp us</span>
    </a>
  );
}
